import React from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import DateRangeSelect from '../common/DateRangeSelect/DateRangeSelect';
import MultiAutocomplete from '../common/MultiAutocomplete/MultiAutocomplete';
import { getSearchWordSuggestions, getPlaceSuggestions } from '../../services/autocompleteService';
import { isUnauthorized } from '../../services/utils/responseValidator';
import trackingConstants from '../../utils/trackingConstants';
import trackingUtils from '../../utils/trackingUtils';
import ButtonSubmit from '../common/ButtonSubmit/ButtonSubmit';
import ButtonClear from '../common/ButtonClear/ButtonClear';
import ButtonSwitch from '../common/ButtonSwitch/ButtonSwitch';
import searchStatsFormStatePropTypes from '../../proptypes/searchStatsFormStatePropTypes';
import suggestionsPropType from '../../proptypes/suggestionsPropType';
import translationPropType from '../../proptypes/translationPropType';

const SearchStatsForm = ({
    handleClear,
    handleLogOut,
    handleSelectWhatSuggestion,
    handleSelectWhereSuggestion,
    handleSubmit,
    handleUpdateFormField,
    isAdvancedUser,
    userLocale,
    searchActive,
    selectedWhatSuggestions,
    selectedWhereSuggestions,
    storeValues,
    translation,
}) => {
    const setField = (field, value) => {
        handleUpdateFormField({ [field]: value });
    };

    const handleCheckEvent = (event) => {
        setField([event.target.name], event.target.checked);
    };

    const handleWordSelect = (handler, field) => (selected) => {
        const words = selected.map(word => word.value).join(',');
        handler(selected);
        handleUpdateFormField({ [field]: words });
    };

    const deactiveAllPlaces = () => {
        if (storeValues.allPlaces) {
            handleUpdateFormField({ allPlaces: false });
        }
    };

    const isValidForm = () => (!!storeValues.what && storeValues.what.length > 1);

    const submit = (event) => {
        if (event) {
            event.preventDefault();
        }
        if (isValidForm()) {
            trackingUtils.trackEvent({
                category: trackingConstants.SEARCH_STATS_SUMBIT,
                action: storeValues.what,
                label: storeValues.allPlaces ? trackingConstants.ALL_PLACES : storeValues.where,
            });
            handleSubmit(storeValues);
        }
    };

    const handleGetSuggestions = serviceMethod => query => serviceMethod(query)
        .catch(err => isUnauthorized(err.response) && handleLogOut(err.message));

    return (
        <form onSubmit={submit} autoComplete="off">
            <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                    <MultiAutocomplete
                        handleSelect={handleWordSelect(handleSelectWhatSuggestion, 'what')}
                        selectedSuggestions={selectedWhatSuggestions}
                        label={`${translation.WHAT} *`}
                        placeholder={translation.WHAT}
                        handleGetSuggestions={
                            handleGetSuggestions(getSearchWordSuggestions)
                        }
                        handleSubmit={submit}
                        creatable
                    />
                </Grid>
                <Grid item xs={12} md={5} onClick={deactiveAllPlaces}>
                    <MultiAutocomplete
                        handleSelect={handleWordSelect(handleSelectWhereSuggestion, 'where')}
                        selectedSuggestions={selectedWhereSuggestions}
                        label={`${translation.WHERE}`}
                        placeholder={translation.WHERE}
                        disabled={storeValues.allPlaces}
                        handleGetSuggestions={
                            handleGetSuggestions(getPlaceSuggestions)
                        }
                        handleSubmit={submit}
                        creatable
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <ButtonSwitch
                        checked={storeValues.allPlaces}
                        label={translation.ALL_PLACES}
                        name="allPlaces"
                        onChange={handleCheckEvent}
                    />
                </Grid>
                <Grid item xs={12} md={5}>
                    <DateRangeSelect
                        to={storeValues.to}
                        locale={userLocale}
                        from={storeValues.from}
                        setValue={setField}
                        translation={translation}
                        disabled={!isAdvancedUser}
                    />
                </Grid>
                <Grid item xs={12}>
                    <ButtonSubmit
                        disabled={!isValidForm()}
                        title={translation.SEARCH}
                    />
                    <ButtonClear
                        disabled={!searchActive}
                        onClick={handleClear}
                        title={translation.CLEAR}
                    />
                </Grid>
            </Grid>
        </form>
    );
};

SearchStatsForm.propTypes = {
    translation: translationPropType.isRequired,
    handleLogOut: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleClear: PropTypes.func.isRequired,
    handleUpdateFormField: PropTypes.func.isRequired,
    handleSelectWhatSuggestion: PropTypes.func.isRequired,
    handleSelectWhereSuggestion: PropTypes.func.isRequired,
    searchActive: PropTypes.bool.isRequired,
    userLocale: PropTypes.string.isRequired,
    storeValues: searchStatsFormStatePropTypes.isRequired,
    selectedWhatSuggestions: suggestionsPropType.isRequired,
    selectedWhereSuggestions: suggestionsPropType.isRequired,
    isAdvancedUser: PropTypes.bool.isRequired,
};

export default SearchStatsForm;
